import { DialogTitle } from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Pencil } from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';

interface EditImageModalProps {
  id: number;
  item: {
    prompt_input: string;
    prompt_type: string;
  };
  handleUpdate?: (
    e: React.FormEvent,
    id: number,
    payload: { prompt_input: string; prompt_type: string }
  ) => void;
}

interface FormData {
  prompt_input: string;
  prompt_type: string;
}

export default function EditImageModal({
  item,
  id,
  handleUpdate,
}: EditImageModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    prompt_input: item.prompt_input,
    prompt_type: item.prompt_type,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when item props change
  useEffect(() => {
    setFormData({
      prompt_input: item.prompt_input,
      prompt_type: item.prompt_type,
    });
  }, [item]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value.trim(),
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.prompt_input.trim()) {
        return; // Don't submit if prompt input is empty
      }

      try {
        setIsSubmitting(true);
        handleUpdate?.(e, id, formData);
        setIsOpen(false);
      } catch (error) {
        console.error('Failed to update image:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleUpdate, id, formData]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    // Reset form data to initial values when canceling
    setFormData({
      prompt_input: item.prompt_input,
      prompt_type: item.prompt_type,
    });
  }, [item]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
      }
      setIsOpen(open);
    },
    [handleCancel]
  );

  const isPromptInputValid = formData.prompt_input.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          className="p-1 flex  w-full  items-center  gap-2 rounded-md text-text-primary lg:text-white  transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          aria-label="Edit image details"
        >
          <Pencil size={16} className="text-primary lg:text-white" />
          <span className="lg:hidden ">Edit</span>
        </button>
      </DialogTrigger>

      <DialogContent className="text-text-primary">
        <DialogHeader>
          <DialogTitle className="text-2xl text-primary font-bold">
            Edit Image Details
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 text-text-primary">
          <div className="space-y-2">
            <label htmlFor="prompt_input" className="text-sm font-medium">
              Prompt Input
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Input
              id="prompt_input"
              name="prompt_input"
              value={formData.prompt_input}
              onChange={handleInputChange}
              onBlur={(e) => {
                e.target.value = e.target.value.trim();
              }}
              type="text"
              required
              aria-required="true"
              aria-invalid={!isPromptInputValid}
              className="w-full"
              placeholder="Enter prompt input"
              maxLength={500}
            />
            {!isPromptInputValid && (
              <p className="text-sm text-red-500" role="alert">
                Prompt input is required
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="prompt_type"
              className="text-sm font-medium  flex items-center"
            >
              Prompt Type
              <span className="ml-2 text-xs ">(Read only)</span>
            </label>
            <Input
              disabled={true}
              id="prompt_type"
              name="prompt_type"
              value={formData.prompt_type}
              onChange={handleInputChange}
              type="text"
              required
              aria-readonly="true"
              className="w-full bg-gray-50"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              onClick={handleCancel}
              variant="customSecondary"
              size="sm"
              className="bg-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="customSecondary"
              size="sm"
              className="bg-secondary"
              disabled={!isPromptInputValid || isSubmitting}
            >
              {isSubmitting ? <> Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
